// Pure helpers for rendering ImportResult(s) into user-facing status
// messages. Extracted from DropOverlay so the reporting shape can be
// unit-tested without simulating DOM drag events.

import type { ImportResult, ImportRejectReason } from '../import';
import type { StatusLevel } from '../../strudel';

export type StatusReport = { message: string; level: StatusLevel };

export function describeResults(results: ImportResult[]): StatusReport | null {
  if (results.length === 0) return null;

  if (results.length === 1) {
    const only = results[0]!;
    if (only.ok) {
      return {
        message: `${only.sample.name} registered — try s("${only.sample.name}").`,
        level: 'ok',
      };
    }
    return { message: 'Import failed: ' + explainRejection(only), level: 'err' };
  }

  const ok = results.filter((r) => r.ok);
  const bad = results.filter((r) => !r.ok);

  if (bad.length === 0) {
    const names = ok.flatMap((r) => (r.ok ? [r.sample.name] : [])).slice(0, 3);
    const more = ok.length > 3 ? ` (+${ok.length - 3} more)` : '';
    return {
      message: `Imported ${ok.length}: ${names.join(', ')}${more}.`,
      level: 'ok',
    };
  }
  if (ok.length === 0) {
    return {
      message: `Import failed: ${bad.length} file(s) rejected (${summariseRejections(bad)}).`,
      level: 'err',
    };
  }
  return {
    message: `Imported ${ok.length}/${results.length}; ${bad.length} rejected (${summariseRejections(bad)}).`,
    level: 'warn',
  };
}

export function explainRejection(r: ImportResult): string {
  if (r.ok) return '';
  const detail = r.detail ? ` (${r.detail})` : '';
  switch (r.reason) {
    case 'not-audio':
      return 'not an audio file' + detail;
    case 'too-large':
      return 'exceeds 8 MB' + detail;
    case 'too-long':
      return 'exceeds 2 min' + detail;
    case 'decode-failed':
      return 'could not decode' + detail;
    case 'empty-name':
      return 'filename has no usable characters';
  }
}

export function summariseRejections(results: ImportResult[]): string {
  const counts = new Map<ImportRejectReason, number>();
  for (const r of results) {
    if (r.ok) continue;
    counts.set(r.reason, (counts.get(r.reason) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([reason, n]) => `${n}× ${reason}`)
    .join(', ');
}
